import { PDFDocument } from 'pdf-lib'

export interface CompressionOptions {
  quality: 'low' | 'medium' | 'high'
  removeMetadata?: boolean
}

export interface CompressionResult {
  compressedBlob: Blob
  originalSize: number
  compressedSize: number
  compressionRatio: number
}

export async function compressPDF(
  file: File,
  options: CompressionOptions = { quality: 'medium', removeMetadata: true }
): Promise<CompressionResult> {
  const originalSize = file.size

  // Load PDF
  const arrayBuffer = await file.arrayBuffer()
  const pdfDoc = await PDFDocument.load(arrayBuffer, {
    updateMetadata: false,
  })

  // Remove metadata if requested
  if (options.removeMetadata) {
    pdfDoc.setTitle('')
    pdfDoc.setAuthor('')
    pdfDoc.setSubject('')
    pdfDoc.setCreator('')
    pdfDoc.setProducer('')
    pdfDoc.setKeywords([])
  }

  // Compress based on quality setting
  const saveOptions = getSaveOptions(options.quality)

  // Save compressed PDF
  const compressedPdfBytes = await pdfDoc.save(saveOptions)
  const compressedBlob = new Blob([new Uint8Array(compressedPdfBytes)], { type: 'application/pdf' })
  const compressedSize = compressedBlob.size

  const compressionRatio = ((originalSize - compressedSize) / originalSize) * 100

  return {
    compressedBlob,
    originalSize,
    compressedSize,
    compressionRatio,
  }
}

function getSaveOptions(quality: 'low' | 'medium' | 'high') {
  switch (quality) {
    case 'low':
      return {
        useObjectStreams: true,
        addDefaultPage: false,
        objectsPerTick: 50,
      }
    case 'medium':
      return {
        useObjectStreams: true,
        addDefaultPage: false,
        objectsPerTick: 100,
      }
    case 'high':
      return {
        useObjectStreams: false,
        addDefaultPage: false,
        objectsPerTick: 200,
      }
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}
